import { Timestamp } from "firebase/firestore";
import { Offline, PaymentMethodDetails } from "./paymentMethod.tsx";
import { Receipt } from "./receipt.tsx";
import { Network } from "./network.tsx";
import { Wallet } from "./wallet.tsx";

export class Card {
    private brand: string;
    private checks: Checks | null | undefined;
    private country: string | null | undefined;
    private display_brand: string | null | undefined;
    private exp_month: number;
    private exp_year: number;
    private fingerprint: string | null | undefined;
    private funding: string;
    private generated_from: GeneratedFrom | null | undefined;
    private last4: string;
    private networks: Network | null | undefined;
    private regulated_status: string | null | undefined;
    private three_d_secure_usage: ThreeDSecureUsage | null | undefined;
    private wallet: Wallet | null | undefined;

    constructor(brand: string, checks: Checks | null | undefined, country: string | null | undefined, display_brand: string | null | undefined, exp_month: number, exp_year: number, fingerprint: string | null | undefined, funding: string, generated_from: GeneratedFrom | null | undefined, last4: string, networks: Network | null | undefined, regulated_status: string | null | undefined, three_d_secure_usage: ThreeDSecureUsage | null | undefined, wallet: Wallet | null | undefined) {
        this.setBrand(brand);
        this.setChecks(checks);
        this.setCountry(country);
        this.setDisplayBrand(display_brand);
        this.setExpMonth(exp_month);
        this.setExpYear(exp_year);
        this.setFingerprint(fingerprint);
        this.setFunding(funding);
        this.setGeneratedFrom(generated_from);
        this.setLast4(last4);
        this.setNetworks(networks);
        this.setRegulatedStatus(regulated_status);
        this.setThreeDSecureUsage(three_d_secure_usage);
        this.setWallet(wallet);
    }

    static fromJson(json:any) {
        return new Card(json.brand, json.checks !== null && json.checks !== undefined ? Checks.fromJson(json.checks) : null, json.country, json.display_brand, json.exp_month, json.exp_year, json.fingerprint, json.funding, json.generated_from !== null && json.generated_from !== undefined ? GeneratedFrom.fromJson(json.generated_from) : null, json.last4, json.networks !== null && json.networks !== undefined ? Network.fromJson(json.networks) : null, json.regulated_status, json.three_d_secure_usage !== null && json.three_d_secure_usage !== undefined ? ThreeDSecureUsage.fromJson(json.three_d_secure_usage) : null, json.wallet !== null && json.wallet !== undefined ? Wallet.fromJson(json.wallet) : null);
    }

    setBrand(newBrand:string) {
        this.brand = newBrand;
    }
    setChecks(newChecks:Checks | null | undefined) {
        this.checks = newChecks;
    }
    setCountry(newCountry:string | null | undefined) {
        this.country = newCountry;
    }
    setDisplayBrand(newDisplayBrand:string | null | undefined) {
        this.display_brand = newDisplayBrand;
    }
    setExpMonth(newExpMonth:number) {
        this.exp_month = newExpMonth;
    }
    setExpYear(newExpYear:number) {
        this.exp_year = newExpYear;
    }
    setFingerprint(newFingerprint:string | null | undefined) {
        this.fingerprint = newFingerprint;
    }
    setFunding(newFunding:string) {
        this.funding = newFunding;
    }
    setGeneratedFrom(newGeneratedFrom:GeneratedFrom | null | undefined) {
        this.generated_from = newGeneratedFrom;
    }
    setLast4(newLast4:string) {
        this.last4 = newLast4;
    }
    setNetworks(newNetworks:Network | null | undefined) {
        this.networks = newNetworks;
    }
    setRegulatedStatus(newRegulatedStatus:string | null | undefined) {
        this.regulated_status = newRegulatedStatus;
    }
    setThreeDSecureUsage(newThreeDSecureUsage:ThreeDSecureUsage | null | undefined) {
        this.three_d_secure_usage = newThreeDSecureUsage;
    }
    setWallet(newWallet:Wallet | null | undefined) {
        this.wallet = newWallet;
    }

    getBrand() {
        return this.brand;
    }
    getChecks() {
        return this.checks;
    }
    getCountry() {
        return this.country;
    }
    getDisplayBrand() {
        return this.display_brand;
    }
    getExpMonth() {
        return this.exp_month;
    }
    getExpYear() {
        return this.exp_year;
    }
    getFingerprint() {
        return this.fingerprint;
    }
    getFunding() {
        return this.funding;
    }
    getGeneratedFrom() {
        return this.generated_from;
    }
    getLast4() {
        return this.last4;
    }
    getNetworks() {
        return this.networks;
    }
    getRegulatedStatus() {
        return this.regulated_status;
    }
    getThreeDSecureUsage() {
        return this.three_d_secure_usage;
    }
    getWallet() {
        return this.wallet;
    }
}

export class Checks {
    private address_line1_check: string | null | undefined;
    private address_postal_code_check: string | null | undefined;
    private cvc_check: string | null | undefined;

    constructor(address_line1_check: string | null | undefined, address_postal_code_check: string | null | undefined, cvc_check: string | null | undefined) {
        this.setAddressLine1Check(address_line1_check);
        this.setAddressPostalCodeCheck(address_postal_code_check);
        this.setCvcCheck(cvc_check);
    }

    static fromJson(json:any) {
        return new Checks(json.address_line1_check, json.address_postal_code_check, json.cvc_check);
    }

    setAddressLine1Check(newAddressLine1Check:string | null | undefined) {
        this.address_line1_check = newAddressLine1Check;
    }
    setAddressPostalCodeCheck(newAddressPostalCodeCheck:string | null | undefined) {
        this.address_postal_code_check = newAddressPostalCodeCheck;
    }
    setCvcCheck(newCvcCheck:string | null | undefined) {
        this.cvc_check = newCvcCheck;
    }

    getAddressLine1Check() {
        return this.address_line1_check;
    }
    getAddressPostalCodeCheck() {
        return this.address_postal_code_check;
    }
    getCvcCheck() {
        return this.cvc_check;
    }
}

export class GeneratedFrom {
    private charge: string | null | undefined;
    private payment_method_details: PaymentMethodDetails | null | undefined;
    private setup_attempt: string | null | undefined;

    constructor(charge: string | null | undefined, payment_method_details: PaymentMethodDetails | null | undefined, setup_attempt: string | null | undefined) {
        this.setCharge(charge);
        this.setPaymentMethodDetails(payment_method_details);
        this.setSetupAttempt(setup_attempt);
    }

    static fromJson(json:any) {
        return new GeneratedFrom(json.charge, json.payment_method_details, json.setup_attempt);
    }

    setCharge(newCharge:string | null | undefined) {
        this.charge = newCharge;
    }
    setPaymentMethodDetails(newPaymentMethodDetails:PaymentMethodDetails | null | undefined) {
        this.payment_method_details = newPaymentMethodDetails;
    }
    setSetupAttempt(newSetupAttempt:string | null | undefined) {
        this.setup_attempt = newSetupAttempt;
    }

    getCharge() {
        return this.charge;
    }
    getPaymentMethodDetails() {
        return this.payment_method_details;
    }
    getSetupAttempt() {
        return this.setup_attempt;
    }
}

export class CardPreset {
    private amount_authorized: number | null | undefined;
    private brand: string | null | undefined;
    private brand_product: string | null | undefined;
    private captureBefore: Timestamp | null | undefined;
    private cardholder_name: string | null | undefined;
    private country: string | null | undefined;
    private description: string | null | undefined;
    private emv_auth_data: string | null | undefined;
    private exp_month: number;
    private exp_year: number;
    private fingerprint: string | null | undefined;
    private funding: string | null | undefined;
    private generated_card: string | null | undefined;
    private incremental_authorization_supported: boolean;
    private issuer: string | null | undefined;
    private last4: string | null | undefined;
    private network: string | null | undefined;
    private network_transaction_id: string | null | undefined;
    private offline: Offline | null | undefined;
    private overcapture_supported: boolean;
    private preferred_locales: string[] | null | undefined
    private read_method: string | null | undefined;
    private receipt: Receipt | null | undefined;

    constructor(amount_authorized: number | null | undefined, brand: string, brand_product: string | null | undefined, captureBefore: Timestamp | null | undefined, cardholder_name: string | null | undefined, country: string | null | undefined, description: string | null | undefined, emv_auth_data: string | null | undefined, exp_month: number, exp_year: number, fingerprint: string | null | undefined, funding: string, generated_card: string | null | undefined, incremental_authorization_supported: boolean, issuer: string | null | undefined, last4: string | null | undefined, network: string | null | undefined, network_transaction_id: string | null | undefined, offline: Offline | null | undefined, overcapture_supported: boolean, preferred_locales: string[] | null | undefined, read_method: string | null | undefined, receipt: Receipt | null | undefined) {
        this.setAmountAuthorized(amount_authorized);
        this.setBrand(brand);
        this.setBrandProduct(brand_product);
        this.setCaptureBefore(captureBefore);
        this.setCardholderName(cardholder_name);
        this.setCountry(country);
        this.setDescription(description);
        this.setEmvAuthData(emv_auth_data);
        this.setExpMonth(exp_month);
        this.setExpYear(exp_year);
        this.setFingerprint(fingerprint);
        this.setFunding(funding);
        this.setGeneratedCard(generated_card);
        this.setIncrementalAuthorizationSupported(incremental_authorization_supported);
        this.setIssuer(issuer);
        this.setLast4(last4);
        this.setNetwork(network);
        this.setNetworkTransactionId(network_transaction_id);
        this.setOffline(offline);
        this.setOvercaptureSupported(overcapture_supported);
        this.setPreferredLocales(preferred_locales);
        this.setReadMethod(read_method);
        this.setReceipt(receipt);
    }

    static fromJson(json:any) {
        return new CardPreset(json.amount_authorized, json.brand, json.brand_product, json.captureBefore, json.cardholder_name, json.country, json.description, json.emv_auth_data, json.exp_month, json.exp_year, json.fingerprint, json.funding, json.generated_card, json.incremental_authorization_supported, json.issuer, json.last4, json.network, json.network_transaction_id, json.offline, json.overcapture_supported, json.preferred_locales, json.read_method, json.receipt);
    }

    setAmountAuthorized(newAmountAuthorized:number | null | undefined) {
        this.amount_authorized = newAmountAuthorized;
    }
    setBrand(newBrand:string) {
        this.brand = newBrand;
    }
    setBrandProduct(newBrandProduct:string | null | undefined) {
        this.brand_product = newBrandProduct;
    }
    setCaptureBefore(newCaptureBefore:Timestamp | null | undefined) {
        this.captureBefore = newCaptureBefore;
    }
    setCardholderName(newCardholderName:string | null | undefined) {
        this.cardholder_name = newCardholderName;
    }
    setCountry(newCountry:string | null | undefined) {
        this.country = newCountry;
    }
    setDescription(newDescription:string | null | undefined) {
        this.description = newDescription;
    }
    setEmvAuthData(newEmvAuthData:string | null | undefined) {
        this.emv_auth_data = newEmvAuthData;
    }
    setExpMonth(newExpMonth:number) {
        this.exp_month = newExpMonth;
    }
    setExpYear(newExpYear:number) {
        this.exp_year = newExpYear;
    }
    setFingerprint(newFingerprint:string | null | undefined) {
        this.fingerprint = newFingerprint;
    }
    setFunding(newFunding:string) {
        this.funding = newFunding;
    }
    setGeneratedCard(newGeneratedCard:string | null | undefined) {
        this.generated_card = newGeneratedCard;
    }
    setIncrementalAuthorizationSupported(newIncrementalAuthorizationSupported:boolean) {
        this.incremental_authorization_supported = newIncrementalAuthorizationSupported;
    }
    setIssuer(newIssuer:string | null | undefined) {
        this.issuer = newIssuer;
    }
    setLast4(newLast4:string | null | undefined) {
        this.last4 = newLast4;
    }
    setNetwork(newNetwork:string | null | undefined) {
        this.network = newNetwork;
    }
    setNetworkTransactionId(newNetworkTransactionId:string | null | undefined) {
        this.network_transaction_id = newNetworkTransactionId;
    }
    setOffline(newOffline:Offline | null | undefined) {
        this.offline = newOffline;
    }
    setOvercaptureSupported(newOvercaptureSupported:boolean) {
        this.overcapture_supported = newOvercaptureSupported;
    }
    setPreferredLocales(newPreferredLocales:string[] | null | undefined) {
        this.preferred_locales = newPreferredLocales;
    }
    setReadMethod(newReadMethod:string | null | undefined) {
        this.read_method = newReadMethod;
    }
    setReceipt(newReceipt:Receipt | null | undefined) {
        this.receipt = newReceipt;
    }

    getAmountAuthorized() {
        return this.amount_authorized;
    }
    getBrand() {
        return this.brand;
    }
    getBrandProduct() {
        return this.brand_product;
    }
    getCaptureBefore() {
        return this.captureBefore;
    }
    getCardholderName() {
        return this.cardholder_name;
    }
    getCountry() {
        return this.country;
    }
    getDescription() {
        return this.description;
    }
    getEmvAuthData() {
        return this.emv_auth_data;
    }
    getExpMonth() {
        return this.exp_month;
    }
    getExpYear() {
        return this.exp_year;
    }
    getFingerprint() {
        return this.fingerprint;
    }
    getFunding() {
        return this.funding;
    }
    getGeneratedCard() {
        return this.generated_card;
    }
    getIncrementalAuthorizationSupported() {
        return this.incremental_authorization_supported;
    }
    getIssuer() {
        return this.issuer;
    }
    getLast4() {
        return this.last4;
    }
    getNetwork() {
        return this.network;
    }
    getNetworkTransactionId() {
        return this.network_transaction_id;
    }
    getOffline() {
        return this.offline;
    }
    getOvercaptureSupported() {
        return this.overcapture_supported;
    }
    getPreferredLocales() {
        return this.preferred_locales;
    }
    getReadMethod() {
        return this.read_method;
    }
    getReceipt() {
        return this.receipt;
    }
}

export class ThreeDSecureUsage {
    private supported: boolean;

    constructor(supported: boolean) {
        this.setSupported(supported);
    }

    static fromJson(json:any) {
        return new ThreeDSecureUsage(json.supported);
    }

    setSupported(newSupported:boolean) {
        this.supported = newSupported;
    }

    getSupported() {
        return this.supported;
    }
}